import React from 'react';
import styled from 'styled-components';

import {
  BaseProps,
  CaptionText,
  FlexColumn,
  FlexRow,
  formatHash,
  formatPercentage,
  HashLength,
  useMatchMedia,
  TruncateBox,
  SvgIcon,
  InputInteractionType,
  useGetInputInteractionType,
  ArrowDownIcon,
} from '@make-software/cspr-design';
import { Tooltip, BodyText } from '@block-explorer/ui';

import Avatar from '../avatar/avatar';
import CsprAmount from '../cspr-amount/cspr-amount';
import CopyButton from '../copy-button/copy-button';
import {
  getAccountInfoLogo,
  ValidatorWithInfo,
} from '@block-explorer/entities';
import { PrecisionCase } from '../../utils/currency';
import { Trans, useTranslation } from 'react-i18next';
import { isValidPublicKey } from '../../utils/account';

const StyledFlexRow = styled(FlexRow)<
  Pick<ValidatorUndelegateInfoRowProps, 'variation' | 'isSelectedItem'> & {
    isHighlighted?: boolean;
  }
>(({ theme, variation = 'transparent', isSelectedItem, isHighlighted }) =>
  theme.withMedia({
    width: '100%',
    padding: ['8px 16px', '8px 16px', '0 16px'],
    minHeight: [76, 76, 48],
    borderRadius: isSelectedItem ? theme.borderRadius.base : null,
    backgroundColor: {
      gray: theme.styleguideColors.fillSecondary,
    }[variation],
    border: {
      transparent: theme.border.base,
      gray: '0',
    }[variation],
    ...(isHighlighted && {
      '&:not(:hover)': {
        border: `1px solid ${theme.styleguideColors.contentBlue}`,
        borderRadius: theme.borderRadius.base,
      },
    }),
  })
);

const KeyContainer = styled(FlexRow)(({ theme }) => ({
  width: '40%',
}));

const FeeContainer = styled.div(({ theme }) => ({
  textAlign: 'right',
  width: '20%',
}));

const StakeContainer = styled.div(({ theme }) => ({
  textAlign: 'right',
  width: '35%',
}));

const StyledCaptionText = styled(CaptionText)(({ theme }) => ({
  lineHeight: '13px',
}));

const ArrowSvgIcon = styled(SvgIcon)(({ theme }) => ({
  path: {
    fill: theme.styleguideColors.contentPrimary,
  },
  marginLeft: 16,
}));

/* eslint-disable-next-line */
export interface ValidatorUndelegateInfoRowProps extends BaseProps {
  validator?: ValidatorWithInfo | null;
  stakedAmountToDelegator: string;
  isCopyEnabled?: boolean;
  showLabel?: boolean;
  label?: string;
  variation?: 'transparent' | 'gray' | 'magenta';
  isSelectedItem?: boolean;
  disabled?: boolean;
  isHighlighted?: boolean;
  handleOpenMenu?: (ev) => void;
}

const ResponsiveValidatorUndelegateRow = React.forwardRef<
  HTMLDivElement,
  Omit<ValidatorUndelegateInfoRowProps, 'validator'> & {
    validator: ValidatorWithInfo;
  }
>((props, ref) => {
  const { t } = useTranslation();
  const responsiveHashSize = useMatchMedia(
    [HashLength.TINY, HashLength.TINY, HashLength.LITTLE, HashLength.LITTLE],
    []
  );

  const {
    isCopyEnabled,
    label = 'Validator',
    showLabel,
    validator,
    stakedAmountToDelegator,
    disabled,
    isSelectedItem,
    isHighlighted,
    handleOpenMenu,
    ...restProps
  } = props;

  const logo = getAccountInfoLogo(validator?.accountInfo);
  const name = validator?.accountInfo?.info?.owner?.name;
  const csprNameOrPublicKey = validator?.csprName || validator?.key;

  const handleKeyDown = (ev) => {
    if (ev.key === 'Enter') {
      handleOpenMenu && handleOpenMenu();
    }
  };

  const inputInteractionTypeRef = useGetInputInteractionType();

  const showA11yKeyboardOutline =
    inputInteractionTypeRef.current === InputInteractionType.keyboard &&
    isHighlighted;

  const onDesktop = (
    <FlexColumn style={{ width: '100%' }}>
      {showLabel && (
        <BodyText scale={'sm'} size={1}>
          {label}
        </BodyText>
      )}
      <StyledFlexRow
        ref={ref}
        align="center"
        itemsSpacing={12}
        {...restProps}
        isHighlighted={showA11yKeyboardOutline}
      >
        <KeyContainer align="center" itemsSpacing={10}>
          {logo ? (
            <Avatar src={logo} size="small" alt={'Validator logo'} />
          ) : (
            <Avatar hash={validator?.key} size="small" />
          )}
          <Tooltip tooltipContent={csprNameOrPublicKey}>
            <FlexColumn>
              {name ? (
                <>
                  <BodyText size={3} monotype={!validator?.csprName}>
                    {isValidPublicKey(csprNameOrPublicKey)
                      ? formatHash(validator?.key, responsiveHashSize)
                      : validator?.csprName}
                  </BodyText>
                  <StyledCaptionText size={2} variation="darkGray">
                    <TruncateBox size={1}>{name}</TruncateBox>
                  </StyledCaptionText>
                </>
              ) : (
                <BodyText size={3} monotype={!validator?.csprName}>
                  {isValidPublicKey(csprNameOrPublicKey)
                    ? formatHash(validator?.key, responsiveHashSize)
                    : validator?.csprName}
                </BodyText>
              )}
            </FlexColumn>
          </Tooltip>
          {isCopyEnabled && <CopyButton value={csprNameOrPublicKey} />}
        </KeyContainer>
        <FeeContainer>
          <FlexColumn>
            <BodyText variation="darkGray" size={3}>
              {validator?.delegationFee != null &&
                formatPercentage(validator?.delegationFee)}
              %
            </BodyText>
            <CaptionText variation="darkGray" size={2}>
              {validator.delegatorCount} <Trans t={t}>delegators</Trans>
            </CaptionText>
          </FlexColumn>
        </FeeContainer>
        <StakeContainer>
          <BodyText size={3} monotype noWrap>
            <CsprAmount
              motes={stakedAmountToDelegator}
              precisionCase={PrecisionCase.deployCost}
            />
          </BodyText>{' '}
        </StakeContainer>
        {!disabled && isSelectedItem ? (
          <ArrowSvgIcon
            src={ArrowDownIcon}
            alt={'Arrow down icon'}
            role={'img'}
            aria-haspopup="listbox"
            aria-expanded={'false'}
            tabIndex={0}
            onKeyDown={handleKeyDown}
          />
        ) : null}
      </StyledFlexRow>
    </FlexColumn>
  );

  const onMobile = (
    <FlexColumn style={{ width: '100%' }}>
      {showLabel && (
        <BodyText scale={'sm'} size={1}>
          {label}
        </BodyText>
      )}
      <StyledFlexRow
        ref={ref}
        {...restProps}
        justify="space-between"
        align={'center'}
        isHighlighted={showA11yKeyboardOutline}
      >
        <FlexRow
          align="center"
          justify="space-between"
          grow={2}
          itemsSpacing={12}
        >
          <FlexRow>
            <FlexColumn itemsSpacing={4}>
              <FlexRow align="center" itemsSpacing={10}>
                {logo ? (
                  <Avatar src={logo} size="small" alt={'Validator logo'} />
                ) : (
                  <Avatar hash={validator?.key} size="small" />
                )}
                <Tooltip tooltipContent={csprNameOrPublicKey}>
                  <FlexColumn>
                    {name ? (
                      <>
                        <FlexRow itemsSpacing={4}>
                          <BodyText size={3} monotype={!validator?.csprName}>
                            {isValidPublicKey(csprNameOrPublicKey)
                              ? formatHash(validator?.key, responsiveHashSize)
                              : validator?.csprName}
                          </BodyText>
                          {isCopyEnabled && (
                            <CopyButton value={csprNameOrPublicKey} />
                          )}
                        </FlexRow>
                        <CaptionText size={2} variation="darkGray">
                          {name}
                        </CaptionText>
                      </>
                    ) : (
                      <FlexRow itemsSpacing={4}>
                        <BodyText size={3} monotype={!validator?.csprName}>
                          {isValidPublicKey(csprNameOrPublicKey)
                            ? formatHash(validator?.key, responsiveHashSize)
                            : validator?.csprName}
                        </BodyText>
                        {isCopyEnabled && (
                          <CopyButton value={csprNameOrPublicKey} />
                        )}
                      </FlexRow>
                    )}
                  </FlexColumn>
                </Tooltip>
              </FlexRow>
              <FlexRow>
                <CaptionText variation="darkGray" size={2}>
                  Fee:{' '}
                  {validator?.delegationFee != null &&
                    formatPercentage(validator?.delegationFee)}
                  % {validator.delegatorCount} <Trans t={t}>delegators</Trans>
                </CaptionText>
              </FlexRow>
            </FlexColumn>
          </FlexRow>
          <FlexRow>
            <FlexColumn align="end">
              <BodyText size={3} monotype noWrap>
                <FlexColumn align="end">
                  <FlexRow>
                    <CsprAmount
                      motes={stakedAmountToDelegator}
                      precisionCase={PrecisionCase.deployCost}
                      hideCsprCurrency
                    />
                  </FlexRow>
                  <FlexRow>CSPR</FlexRow>
                </FlexColumn>
              </BodyText>{' '}
            </FlexColumn>
          </FlexRow>
        </FlexRow>
        {!disabled && isSelectedItem ? (
          <ArrowSvgIcon src={ArrowDownIcon} alt={'Arrow down icon'} />
        ) : null}
      </StyledFlexRow>
    </FlexColumn>
  );

  const responsiveValidatorUndelegateInfoRow = useMatchMedia<any>(
    [onMobile, onMobile, onDesktop, onDesktop],
    [validator, stakedAmountToDelegator, isHighlighted]
  );

  return responsiveValidatorUndelegateInfoRow;
});

export const ValidatorUndelegateInfoRow = React.forwardRef<
  HTMLDivElement,
  ValidatorUndelegateInfoRowProps
>((props: ValidatorUndelegateInfoRowProps, ref) => {
  if (!props.validator) {
    return null;
  }

  return (
    <ResponsiveValidatorUndelegateRow
      {...props}
      validator={props.validator}
      ref={ref}
    />
  );
});

export default ValidatorUndelegateInfoRow;
